import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import ArrowBack from '@mui/icons-material/ArrowBack';
import AddPhotoAlternate from '@mui/icons-material/AddPhotoAlternate';
import Edit from '@mui/icons-material/Edit';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import PageHeader from '../../../Components/PageHeader';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getOnlineItem,
  uploadOnlineItemImage,
  addOnlineItemImage,
  deleteOnlineItemImage,
  deleteOnlineItem,
  updateOnlineItem,
  getCategories,
  getGroupTypes,
  listItems,
} from '../../../endpoints/chapterSupplies';
import { getMyPermissions } from '../../../endpoints/usersTs';
import { getDocumentList } from '../../../endpoints/documents';
import type { UpdateOnlineItemRequest } from '../../../types/chapterSupplies';
import type { DocumentList } from '../../../types/document';

const ITEM_TYPE_OPTIONS = ['individual', 'kit'] as const;

const ITEM_TYPE_LABELS: Record<string, string> = {
  individual: 'Individual',
  kit: 'Kit',
  variant: 'Variant',
};

function onlineItemVariantParent(
  itemType: string,
  hasVariants?: number | null
): boolean {
  return (
    itemType === 'variant' ||
    (itemType === 'individual' && Number(hasVariants ?? 0) !== 0)
  );
}

const DOCUMENT_SEARCH_DEBOUNCE_MS = 300;

function selectedNames(
  ids: string[],
  options: { id: string; name: string }[],
  maxShow = 3
): string {
  if (!ids.length) return 'None';
  const names = ids
    .map((id) => options.find((o) => o.id === id)?.name)
    .filter(Boolean) as string[];
  if (names.length > maxShow) {
    return `${names.slice(0, maxShow).join(', ')} +${names.length - maxShow} more`;
  }
  return names.join(', ');
}

export default function OnlineItemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [documentSearchInput, setDocumentSearchInput] = useState('');
  const [documentSearch, setDocumentSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(
      () => setDocumentSearch(documentSearchInput),
      DOCUMENT_SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [documentSearchInput]);

  const {
    data: detail,
    isFetching,
    refetch,
  } = useApiGet(
    ['chapter-supplies-online-item', id],
    () => getOnlineItem(id!),
    { enabled: !!id, retry: 1 }
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [newImageSortOrder, setNewImageSortOrder] = useState(0);
  const [editForm, setEditForm] = useState<UpdateOnlineItemRequest>({
    category_ids: [],
    sub_category_ids: [],
    group_type_ids: [],
  });

  const { data: categories = [] } = useApiGet(
    ['chapter-supplies-categories'],
    getCategories,
    { enabled: !!id, retry: 1 }
  );
  const { data: groupTypes = [] } = useApiGet(
    ['chapter-supplies-group-types'],
    getGroupTypes,
    { enabled: !!id, retry: 1 }
  );
  const { data: myPermissions = [] } = useApiGet(
    ['my-permissions'],
    () => getMyPermissions(),
    { enabled: true, retry: 1 }
  );
  const { data: myInternationalPermissions = [] } = useApiGet(
    ['my-permissions-international'],
    () => getMyPermissions({ international: 'true' }),
    { enabled: true, retry: 1 }
  );
  const { data: itemsData } = useApiGet(
    ['chapter-supplies-items', 500, 0],
    () => listItems({ limit: 500, offset: 0 }),
    { enabled: !!id && editing, retry: 1 }
  );
  const allSubcategories = useMemo(
    () => categories.flatMap((c) => c.subcategories ?? []),
    [categories]
  );
  const { data: documentsDataSearch } = useApiGet(
    ['library-documents-search', documentSearch],
    () =>
      getDocumentList({
        search: documentSearch || undefined,
        per_page: 50,
        order: 'asc',
        sort_by: 'name',
      }),
    { enabled: !!id && editing, retry: 1 }
  );
  const { data: documentsDataView } = useApiGet(
    ['library-documents-for-selector'],
    () => getDocumentList({ per_page: 100, order: 'asc', sort_by: 'name' }),
    { enabled: !!id && !editing && !!detail?.library_document_id, retry: 1 }
  );
  const libraryDocuments: DocumentList[] = editing
    ? (documentsDataSearch?.data ?? [])
    : (documentsDataView?.data ?? []);

  const effectiveItemType =
    editForm.item_type ?? detail?.item_type ?? 'individual';
  const effectiveHasVariants =
    editForm.has_variants ?? detail?.has_variants ?? 0;
  const variantParent = onlineItemVariantParent(
    effectiveItemType,
    effectiveHasVariants
  );
  const effectiveAllowsCustomText =
    editForm.allows_custom_text ?? detail?.allows_custom_text ?? 0;
  const effectiveAllowsPersonalization =
    editForm.allows_personalization ?? detail?.allows_personalization ?? 0;
  const requiresMaxTextLength =
    effectiveItemType !== 'kit' &&
    (Number(effectiveAllowsCustomText) !== 0 ||
      Number(effectiveAllowsPersonalization) !== 0);
  const categoriesRequired = effectiveItemType === 'individual';
  const { mutate: doUploadAndAdd } = useApiSend(
    async (file: File) => {
      const { url } = await uploadOnlineItemImage(file);
      if (!url?.trim()) {
        throw new Error('Upload did not return a URL');
      }
      await addOnlineItemImage(id!, {
        image_url: url,
        sort_order: newImageSortOrder,
      });
    },
    () => {
      setUploading(false);
      setUploadError(null);
      refetch();
    },
    (err) => {
      setUploading(false);
      setUploadError(err?.message ?? 'Upload failed');
    },
    [['chapter-supplies-online-item', id]]
  );

  const { mutate: doUpdate, isPending: isUpdating } = useApiSend(
    (payload: UpdateOnlineItemRequest) => updateOnlineItem(id!, payload),
    () => {
      setEditError(null);
      setEditing(false);
      refetch();
    },
    (err) => {
      setEditError(err?.message ?? 'Update failed');
    },
    [['chapter-supplies-online-item', id]]
  );

  const { mutate: doRemoveImage, isPending: isRemovingImage } = useApiSend(
    (imageId: string) => deleteOnlineItemImage(id!, imageId),
    () => {
      setUploadError(null);
      refetch();
    },
    (err) => setUploadError(err?.message ?? 'Failed to remove image'),
    [['chapter-supplies-online-item', id]]
  );
  const { mutate: doDeleteOnlineItem, isPending: isDeleting } = useApiSend(
    (_: void) => deleteOnlineItem(id!),
    () => {
      navigate('/chapter-supplies-management/online-items');
    },
    (err) => setDeleteError(err?.message ?? 'Failed to delete online item'),
    [['chapter-supplies-online-items']]
  );

  const handleAddImageClick = () => fileInputRef.current?.click();
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    e.target.value = '';
    setUploadError(null);
    setUploading(true);
    doUploadAndAdd(file);
  };

  useEffect(() => {
    if (detail?.images) {
      setNewImageSortOrder(detail.images.length);
    }
  }, [detail?.images]);

  if (!id) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Typography color="error">Missing online item ID.</Typography>
      </Grid>
    );
  }

  if (isFetching && !detail) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Typography>Loading…</Typography>
      </Grid>
    );
  }

  if (!detail) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Typography color="error">Online item not found.</Typography>
      </Grid>
    );
  }
  const effectivePermissions = new Set<string>([
    ...(myPermissions ?? []),
    ...(myInternationalPermissions ?? []),
  ]);
  const canDeleteOnlineItem = effectivePermissions.has('orders:item_delete');

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader
          title={
            detail.item_name ??
            (detail.item_type
              ? `Online item (${ITEM_TYPE_LABELS[detail.item_type] ?? detail.item_type})`
              : 'Online item')
          }
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() =>
                navigate('/chapter-supplies-management/online-items')
              }
            >
              Back to list
            </Button>
          }
          rightDom={
            editing ? (
              <Button
                variant="outlined"
                onClick={() => {
                  setEditing(false);
                  setEditError(null);
                }}
              >
                Cancel
              </Button>
            ) : (
              <Stack direction="row" spacing={1}>
                {canDeleteOnlineItem && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutline />}
                    disabled={isDeleting}
                    onClick={() => {
                      setDeleteError(null);
                      const ok = window.confirm(
                        'Delete this online item permanently? This cannot be undone.'
                      );
                      if (!ok) return;
                      doDeleteOnlineItem(undefined);
                    }}
                  >
                    {isDeleting ? 'Deleting…' : 'Delete'}
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  disabled={detail.item_type === 'variant'}
                  onClick={() => {
                    setEditing(true);
                    setEditError(null);
                    setEditForm({
                      item_id: detail.item_id ?? null,
                      item_type: detail.item_type ?? null,
                      price: detail.price,
                      is_featured: detail.is_featured,
                      is_active: detail.is_active,
                      name: detail.name ?? null,
                      short_description: detail.short_description ?? null,
                      long_description: detail.long_description ?? null,
                      tags: detail.tags ?? null,
                      sort_order: detail.sort_order,
                      library_document_id:
                        detail.item_type === 'kit' ||
                        onlineItemVariantParent(
                          detail.item_type ?? '',
                          detail.has_variants
                        )
                          ? null
                          : (detail.library_document_id ?? null),
                      max_quantity_per_order:
                        detail.max_quantity_per_order ?? null,
                      max_text_length: detail.max_text_length ?? null,
                      max_text_lines: detail.max_text_lines ?? null,
                      category_ids: detail.category_ids ?? [],
                      sub_category_ids: detail.sub_category_ids ?? [],
                      group_type_ids: detail.group_type_ids ?? [],
                      allows_custom_text: detail.allows_custom_text ?? 0,
                      allows_personalization:
                        detail.allows_personalization ?? 0,
                      has_variants: detail.has_variants ?? 0,
                      kit_components:
                        detail.item_type === 'kit'
                          ? (detail.kit_components ?? []).map((k) => ({
                              item_id: k.item_id,
                              quantity: k.quantity,
                            }))
                          : [],
                      variants: onlineItemVariantParent(
                        detail.item_type ?? '',
                        detail.has_variants
                      )
                        ? (detail.variants ?? []).map((v) => ({
                            item_id: v.item_id ?? null,
                            price: v.price ?? 0,
                            description: v.description ?? null,
                          }))
                        : [],
                      order_approval_required:
                        detail.order_approval_required ?? 0,
                    });
                  }}
                >
                  Edit
                </Button>
              </Stack>
            )
          }
          subtitle={undefined}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Paper sx={{ p: 2 }}>
          {editing ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setEditError(null);
                const effType =
                  editForm.item_type ?? detail.item_type ?? 'individual';
                let coerceAllowsCT =
                  editForm.allows_custom_text ?? detail.allows_custom_text ?? 0;
                let coerceAllowsP =
                  editForm.allows_personalization ??
                  detail.allows_personalization ??
                  0;
                let coerceHasV =
                  editForm.has_variants ?? detail.has_variants ?? 0;
                if (effType === 'kit') {
                  coerceAllowsCT = 0;
                  coerceAllowsP = 0;
                  coerceHasV = 0;
                }
                if (effType === 'variant') {
                  coerceHasV = 1;
                }
                const effVariantParent = onlineItemVariantParent(
                  effType,
                  coerceHasV
                );
                const allowsTextEffective =
                  effType !== 'kit' &&
                  (Number(coerceAllowsCT) !== 0 || Number(coerceAllowsP) !== 0);

                const payload: UpdateOnlineItemRequest = {
                  ...editForm,
                  allows_custom_text: coerceAllowsCT,
                  allows_personalization: coerceAllowsP,
                  has_variants: coerceHasV,
                  max_text_length: allowsTextEffective
                    ? (editForm.max_text_length ??
                      detail.max_text_length ??
                      null)
                    : null,
                  max_text_lines: allowsTextEffective
                    ? (editForm.max_text_lines ?? detail.max_text_lines ?? null)
                    : null,
                };
                if (effVariantParent) {
                  payload.price = 0;
                }

                const effectivePrice =
                  payload.price !== undefined && payload.price !== null
                    ? Number(payload.price)
                    : Number(detail.price ?? 0);
                const effectiveShortDesc = (
                  payload.short_description ??
                  detail.short_description ??
                  ''
                )
                  .toString()
                  .trim();
                const effectiveName = (
                  payload.name ??
                  detail.name ??
                  ''
                ).toString();
                const effectiveCategoryIds =
                  payload.category_ids ?? detail.category_ids ?? [];
                const effectiveMaxTextLength = Number(
                  payload.max_text_length ?? detail.max_text_length ?? 0
                );
                const effectiveMaxTextLines = Number(
                  payload.max_text_lines ?? detail.max_text_lines ?? 0
                );

                if (categoriesRequired && !effectiveCategoryIds.length) {
                  setEditError(
                    'At least one category is required for individual items'
                  );
                  return;
                }
                if (
                  effType === 'individual' &&
                  !effVariantParent &&
                  effectivePrice < 0
                ) {
                  setEditError('Price must be 0 or greater');
                  return;
                }
                if (
                  effType === 'individual' &&
                  !effVariantParent &&
                  !effectiveShortDesc
                ) {
                  setEditError(
                    'Short description is required for individual items'
                  );
                  return;
                }
                if (
                  effType === 'individual' &&
                  effVariantParent &&
                  !effectiveShortDesc
                ) {
                  setEditError(
                    'Short description is required for individual items with variants'
                  );
                  return;
                }
                if (allowsTextEffective) {
                  if (effectiveMaxTextLength <= 0) {
                    setEditError(
                      'Max text length is required when customization is enabled'
                    );
                    return;
                  }
                  if (effectiveMaxTextLines <= 0) {
                    setEditError(
                      'Max text lines is required when customization is enabled'
                    );
                    return;
                  }
                }
                if (effType === 'kit') {
                  if (!effectiveName.trim()) {
                    setEditError('Name (kit name) is required for kit items');
                    return;
                  }
                  const kitComps =
                    payload.kit_components ?? detail.kit_components ?? [];
                  const validKit = kitComps.filter(
                    (k: { item_id?: string; quantity?: number }) =>
                      k.item_id?.trim() && (k.quantity ?? 0) > 0
                  );
                  if (!validKit.length) {
                    setEditError(
                      'Kit must have at least one component with an item and quantity'
                    );
                    return;
                  }
                }
                if (effVariantParent) {
                  if (!effectiveName.trim()) {
                    setEditError('Name is required for items with variants');
                    return;
                  }
                  const vsPayload = payload.variants;
                  if (vsPayload !== undefined && vsPayload !== null) {
                    const validVariants = vsPayload.filter(
                      (v: { price?: number }) => (v.price ?? 0) >= 0
                    );
                    if (!validVariants.length) {
                      setEditError(
                        'Items with variants must have at least one option with price 0 or greater'
                      );
                      return;
                    }
                    payload.variants = vsPayload.map((v) => ({
                      ...v,
                      description:
                        v.description != null &&
                        String(v.description).trim() !== ''
                          ? String(v.description).trim()
                          : null,
                    }));
                  } else if (!(detail.variants ?? []).length) {
                    setEditError(
                      'Items with variants must have at least one variant option'
                    );
                    return;
                  }
                }
                doUpdate(payload);
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="edit-item-type-label">Item type</InputLabel>
                    <Select
                      labelId="edit-item-type-label"
                      label="Item type"
                      value={
                        editForm.item_type ?? detail.item_type ?? 'individual'
                      }
                      onChange={(e) =>
                        setEditForm((prev) => {
                          const v = e.target.value as string;
                          const next = { ...prev, item_type: v };
                          if (v === 'individual') {
                            next.has_variants = 0;
                            next.variants = [];
                          }
                          if (v === 'kit') {
                            next.allows_custom_text = 0;
                            next.allows_personalization = 0;
                            next.has_variants = 0;
                            next.variants = [];
                            next.library_document_id = null;
                            next.name = detail.name ?? null;
                            next.kit_components =
                              (detail.kit_components ?? []).length > 0
                                ? (detail.kit_components ?? []).map((k) => ({
                                    item_id: k.item_id,
                                    quantity: k.quantity ?? 1,
                                  }))
                                : [{ item_id: '', quantity: 1 }];
                          }
                          return next;
                        })
                      }
                    >
                      {ITEM_TYPE_OPTIONS.map((t) => (
                        <MenuItem key={t} value={t}>
                          {ITEM_TYPE_LABELS[t] ?? t.replace('_', ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {effectiveItemType === 'individual' && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Item (read-only)
                    </Typography>
                    <Typography variant="body1">
                      {detail.item_id
                        ? `${detail.item_name ?? '—'} (${detail.item_id})`
                        : '—'}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required={
                      effectiveItemType === 'kit' ||
                      (effectiveItemType === 'individual' && !variantParent)
                    }
                    disabled={
                      effectiveItemType === 'individual' && variantParent
                    }
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    label="Price"
                    helperText={
                      effectiveItemType === 'individual' && variantParent
                        ? 'Managed by variant prices'
                        : undefined
                    }
                    value={
                      effectiveItemType === 'individual' && variantParent
                        ? ''
                        : (editForm.price ?? '')
                    }
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        price:
                          e.target.value === ''
                            ? undefined
                            : Number(e.target.value),
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(editForm.is_featured ?? 0) === 1}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            is_featured: e.target.checked ? 1 : 0,
                          }))
                        }
                      />
                    }
                    label="Featured"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(editForm.is_active ?? 1) === 1}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            is_active: e.target.checked ? 1 : 0,
                          }))
                        }
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!(editForm.order_approval_required ?? 0)}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            order_approval_required: e.target.checked ? 1 : 0,
                          }))
                        }
                      />
                    }
                    label="Order approval required"
                  />
                </Grid>
                {effectiveItemType !== 'kit' && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={(editForm.has_variants ?? 0) === 1}
                            onChange={(e) => {
                              const on = e.target.checked ? 1 : 0;
                              setEditForm((prev) => ({
                                ...prev,
                                has_variants: on,
                                variants:
                                  on && !(prev.variants?.length ?? 0)
                                    ? [
                                        {
                                          item_id: null,
                                          price: 0,
                                          description: null,
                                        },
                                      ]
                                    : on
                                      ? prev.variants
                                      : [],
                              }));
                            }}
                          />
                        }
                        label="Has variant options"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={(editForm.allows_custom_text ?? 0) === 1}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                allows_custom_text: e.target.checked ? 1 : 0,
                              }))
                            }
                          />
                        }
                        label="Allows custom text"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              (editForm.allows_personalization ?? 0) === 1
                            }
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                allows_personalization: e.target.checked
                                  ? 1
                                  : 0,
                              }))
                            }
                          />
                        }
                        label="Require Member Selection"
                      />
                    </Grid>
                  </>
                )}
                {effectiveItemType === 'kit' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Kit name"
                      value={editForm.name ?? ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          name: e.target.value === '' ? null : e.target.value,
                        }))
                      }
                    />
                  </Grid>
                )}
                {effectiveItemType === 'kit' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Short description"
                      value={editForm.short_description ?? ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          short_description: e.target.value || null,
                        }))
                      }
                    />
                  </Grid>
                )}
                {effectiveItemType === 'individual' && variantParent && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Name"
                        value={editForm.name ?? ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            name: e.target.value === '' ? null : e.target.value,
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        multiline
                        rows={2}
                        label="Short description"
                        value={editForm.short_description ?? ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            short_description: e.target.value || null,
                          }))
                        }
                      />
                    </Grid>
                  </>
                )}
                {effectiveItemType === 'individual' && !variantParent && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={2}
                      label="Short description"
                      value={editForm.short_description ?? ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          short_description: e.target.value || null,
                        }))
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Long description"
                    value={editForm.long_description ?? ''}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        long_description: e.target.value || null,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Tags"
                    placeholder="Comma-separated for search"
                    value={editForm.tags ?? ''}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        tags: e.target.value || null,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Images
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}
                  >
                    {(detail.images ?? []).map((img) => (
                      <Box
                        key={img.id}
                        sx={{
                          position: 'relative',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          component="img"
                          src={img.image_url}
                          alt=""
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ px: 0.5, py: 0.25 }}
                        >
                          Order: {img.sort_order}
                        </Typography>
                        <Button
                          type="button"
                          size="small"
                          color="error"
                          startIcon={<DeleteOutline />}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            doRemoveImage(img.id);
                          }}
                          disabled={isRemovingImage}
                          sx={{ minWidth: 0, py: 0.25 }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 1 }}
                        label="Sort order for new image"
                        sx={{ width: 160 }}
                        value={newImageSortOrder}
                        onChange={(e) =>
                          setNewImageSortOrder(
                            e.target.value === '' ? 0 : Number(e.target.value)
                          )
                        }
                      />
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        startIcon={<AddPhotoAlternate />}
                        onClick={(e) => {
                          e.preventDefault();
                          handleAddImageClick();
                        }}
                        disabled={uploading}
                      >
                        {uploading ? 'Uploading…' : 'Add image'}
                      </Button>
                    </Box>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                  </Box>
                  {uploadError && (
                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                      {uploadError}
                    </Typography>
                  )}
                </Grid>
                {effectiveItemType !== 'kit' && requiresMaxTextLength && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required={requiresMaxTextLength}
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      label="Max text length"
                      value={editForm.max_text_length ?? ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          max_text_length:
                            e.target.value === ''
                              ? null
                              : Number(e.target.value),
                        }))
                      }
                    />
                  </Grid>
                )}
                {effectiveItemType !== 'kit' && requiresMaxTextLength && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required={requiresMaxTextLength}
                      type="number"
                      inputProps={{ min: 1, step: 1 }}
                      label="Max text lines"
                      value={editForm.max_text_lines ?? ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          max_text_lines:
                            e.target.value === ''
                              ? null
                              : Number(e.target.value),
                        }))
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    label="Sort order"
                    value={editForm.sort_order ?? ''}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        sort_order:
                          e.target.value === ''
                            ? undefined
                            : Number(e.target.value),
                      }))
                    }
                  />
                </Grid>
                {effectiveItemType === 'individual' && !variantParent && (
                  <Grid item xs={12} sm={6}>
                    <Autocomplete<DocumentList>
                      fullWidth
                      options={libraryDocuments}
                      getOptionLabel={(opt) => opt.name}
                      value={
                        editForm.library_document_id
                          ? (libraryDocuments.find(
                              (d) => d.id === editForm.library_document_id
                            ) ??
                            ({
                              id: editForm.library_document_id,
                              name: editForm.library_document_id,
                            } as DocumentList))
                          : null
                      }
                      onChange={(_, newValue) =>
                        setEditForm((prev) => ({
                          ...prev,
                          library_document_id: newValue?.id ?? null,
                        }))
                      }
                      onInputChange={(_, value) =>
                        setDocumentSearchInput(value)
                      }
                      filterOptions={(x) => x}
                      isOptionEqualToValue={(opt, val) => opt.id === val.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Library document"
                          placeholder="Search documents..."
                        />
                      )}
                    />
                  </Grid>
                )}
                {effectiveItemType === 'kit' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Kit components
                    </Typography>
                    {(editForm.kit_components ?? []).map((comp, idx) => (
                      <Grid
                        container
                        key={idx}
                        spacing={2}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Grid item xs={12} sm={5}>
                          <FormControl fullWidth size="small">
                            <InputLabel id={`edit-kit-item-${idx}`}>
                              Item
                            </InputLabel>
                            <Select
                              labelId={`edit-kit-item-${idx}`}
                              label="Item"
                              value={comp.item_id ?? ''}
                              onChange={(e) => {
                                const next = [
                                  ...(editForm.kit_components ?? []),
                                ];
                                next[idx] = {
                                  ...next[idx],
                                  item_id: e.target.value,
                                  quantity: next[idx]?.quantity ?? 1,
                                };
                                setEditForm((prev) => ({
                                  ...prev,
                                  kit_components: next,
                                }));
                              }}
                            >
                              <MenuItem value="">
                                <em>Select item</em>
                              </MenuItem>
                              {(itemsData?.items ?? []).map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                  {item.name}
                                  {item.sku ? ` (${item.sku})` : ''}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, step: 1 }}
                            label="Quantity"
                            value={comp.quantity ?? 1}
                            onChange={(e) => {
                              const next = [...(editForm.kit_components ?? [])];
                              next[idx] = {
                                ...next[idx],
                                item_id: next[idx]?.item_id ?? '',
                                quantity:
                                  e.target.value === ''
                                    ? 1
                                    : Number(e.target.value),
                              };
                              setEditForm((prev) => ({
                                ...prev,
                                kit_components: next,
                              }));
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => {
                              const next = (
                                editForm.kit_components ?? []
                              ).filter((_, i) => i !== idx);
                              setEditForm((prev) => ({
                                ...prev,
                                kit_components: next,
                              }));
                            }}
                          >
                            Remove
                          </Button>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        setEditForm((prev) => ({
                          ...prev,
                          kit_components: [
                            ...(prev.kit_components ?? []),
                            { item_id: '', quantity: 1 },
                          ],
                        }))
                      }
                    >
                      Add component
                    </Button>
                  </Grid>
                )}
                {variantParent && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Variants
                    </Typography>
                    {(editForm.variants ?? []).map((vr, idx) => {
                      const linkedInv = (itemsData?.items ?? []).find(
                        (it) => it.id === (vr.item_id ?? '')
                      );
                      const fromCatalog =
                        linkedInv?.images?.[0]?.image_url?.trim() ?? '';
                      const fromSaved =
                        detail.variants?.[idx]?.item_id === vr.item_id
                          ? detail.variants[idx]?.item_image_url
                          : undefined;
                      const variantItemPreview =
                        fromCatalog || fromSaved?.trim() || '';
                      return (
                        <Grid
                          container
                          key={idx}
                          spacing={2}
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel id={`edit-variant-item-${idx}`}>
                                Item (optional)
                              </InputLabel>
                              <Select
                                labelId={`edit-variant-item-${idx}`}
                                label="Item (optional)"
                                value={vr.item_id ?? ''}
                                onChange={(e) => {
                                  const next = [...(editForm.variants ?? [])];
                                  next[idx] = {
                                    ...next[idx],
                                    item_id: e.target.value || null,
                                    price: next[idx]?.price ?? 0,
                                    description: next[idx]?.description ?? null,
                                  };
                                  setEditForm((prev) => ({
                                    ...prev,
                                    variants: next,
                                  }));
                                }}
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {(itemsData?.items ?? []).map((item) => (
                                  <MenuItem key={item.id} value={item.id}>
                                    {item.name}
                                    {item.sku ? ` (${item.sku})` : ''}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          {variantItemPreview ? (
                            <Grid item xs="auto">
                              <Box
                                component="img"
                                src={variantItemPreview}
                                alt=""
                                title="Image from linked inventory item"
                                sx={{
                                  width: 40,
                                  height: 40,
                                  objectFit: 'contain',
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: 'divider',
                                }}
                              />
                            </Grid>
                          ) : null}
                          <Grid item xs={12} sm={3}>
                            <TextField
                              size="small"
                              fullWidth
                              required
                              type="number"
                              inputProps={{ min: 0, step: 0.01 }}
                              label="Price"
                              value={vr.price ?? 0}
                              onChange={(e) => {
                                const next = [...(editForm.variants ?? [])];
                                next[idx] = {
                                  ...next[idx],
                                  item_id: next[idx]?.item_id ?? null,
                                  price:
                                    e.target.value === ''
                                      ? 0
                                      : Number(e.target.value),
                                  description: next[idx]?.description ?? null,
                                };
                                setEditForm((prev) => ({
                                  ...prev,
                                  variants: next,
                                }));
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              minRows={1}
                              label="Description (optional)"
                              value={vr.description ?? ''}
                              onChange={(e) => {
                                const next = [...(editForm.variants ?? [])];
                                const v = e.target.value;
                                next[idx] = {
                                  ...next[idx],
                                  item_id: next[idx]?.item_id ?? null,
                                  price: next[idx]?.price ?? 0,
                                  description: v === '' ? null : v,
                                };
                                setEditForm((prev) => ({
                                  ...prev,
                                  variants: next,
                                }));
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => {
                                const next = (editForm.variants ?? []).filter(
                                  (_, i) => i !== idx
                                );
                                setEditForm((prev) => ({
                                  ...prev,
                                  variants: next,
                                }));
                              }}
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      );
                    })}
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        setEditForm((prev) => ({
                          ...prev,
                          variants: [
                            ...(prev.variants ?? []),
                            { item_id: null, price: 0, description: null },
                          ],
                        }))
                      }
                    >
                      Add variant
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    label="Max quantity per order"
                    value={editForm.max_quantity_per_order ?? ''}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        max_quantity_per_order:
                          e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required={categoriesRequired}>
                    <InputLabel id="edit-categories-label">
                      Categories
                    </InputLabel>
                    <Select
                      labelId="edit-categories-label"
                      label="Categories"
                      multiple
                      value={editForm.category_ids ?? []}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          category_ids: e.target.value as string[],
                        }))
                      }
                      renderValue={(selected) =>
                        selectedNames(selected as string[], categories)
                      }
                    >
                      {categories.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="edit-subcategories-label">
                      Subcategories
                    </InputLabel>
                    <Select
                      labelId="edit-subcategories-label"
                      label="Subcategories"
                      multiple
                      value={editForm.sub_category_ids ?? []}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          sub_category_ids: e.target.value as string[],
                        }))
                      }
                      renderValue={(selected) =>
                        selectedNames(selected as string[], allSubcategories)
                      }
                    >
                      {allSubcategories.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="edit-restrictions-label">
                      Restrictions
                    </InputLabel>
                    <Select
                      labelId="edit-restrictions-label"
                      label="Restrictions"
                      multiple
                      value={editForm.group_type_ids ?? []}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          group_type_ids: e.target.value as string[],
                        }))
                      }
                      renderValue={(selected) =>
                        selectedNames(selected as string[], groupTypes)
                      }
                    >
                      {groupTypes.map((g) => (
                        <MenuItem key={g.id} value={g.id}>
                          {g.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {editError && (
                  <Grid item xs={12}>
                    <Typography color="error" variant="body2">
                      {editError}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'Saving…' : 'Save changes'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          ) : (
            <Grid container spacing={2}>
              {deleteError && (
                <Grid item xs={12}>
                  <Typography color="error" variant="body2">
                    {deleteError}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Item type
                </Typography>
                <Typography variant="body1">
                  {ITEM_TYPE_LABELS[detail.item_type ?? ''] ??
                    detail.item_type ??
                    '—'}
                </Typography>
              </Grid>
              {detail.item_type === 'individual' && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Item (linked inventory item)
                  </Typography>
                  <Typography variant="body1">
                    {detail.item_id
                      ? `${detail.item_name ?? '—'} (${detail.item_id})`
                      : '—'}
                  </Typography>
                </Grid>
              )}
              {detail.item_type === 'kit' &&
                (detail.kit_components ?? []).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Kit components
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                      {(detail.kit_components ?? []).map((k, idx) => (
                        <li key={idx}>
                          <Typography variant="body2">
                            {k.item_name ?? k.item_id ?? '—'}: {k.quantity ?? 0}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  </Grid>
                )}
              {detail.item_type === 'kit' && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Name (Kit name)
                  </Typography>
                  <Typography variant="body1">{detail.name ?? '—'}</Typography>
                </Grid>
              )}
              {(detail.item_type === 'variant' ||
                (detail.item_type === 'individual' &&
                  (detail.has_variants ?? 0) !== 0)) && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">{detail.name ?? '—'}</Typography>
                </Grid>
              )}
              {onlineItemVariantParent(
                detail.item_type ?? '',
                detail.has_variants
              ) &&
                (detail.variants ?? []).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Variants
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                      {(detail.variants ?? []).map((v, idx) => (
                        <li key={idx}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {v.item_image_url ? (
                              <Box
                                component="img"
                                src={v.item_image_url}
                                alt=""
                                sx={{
                                  width: 36,
                                  height: 36,
                                  objectFit: 'contain',
                                  borderRadius: 0.5,
                                  flexShrink: 0,
                                }}
                              />
                            ) : null}
                            <Typography variant="body2" component="span">
                              {v.item_name ?? v.item_id ?? '—'}: $
                              {(v.price ?? 0).toFixed(2)}
                              {v.description ? ` — ${v.description}` : ''}
                            </Typography>
                          </Box>
                        </li>
                      ))}
                    </Box>
                  </Grid>
                )}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Price
                </Typography>
                <Typography variant="body1">
                  {onlineItemVariantParent(
                    detail.item_type ?? '',
                    detail.has_variants
                  )
                    ? '—'
                    : typeof detail.price === 'number'
                      ? `$${detail.price.toFixed(2)}`
                      : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Featured
                </Typography>
                <Typography variant="body1">
                  {detail.is_featured === 1 ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Active
                </Typography>
                <Typography variant="body1">
                  {detail.is_active === 1 ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Order approval required
                </Typography>
                <Typography variant="body1">
                  {detail.order_approval_required ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Short description
                </Typography>
                <Typography variant="body1">
                  {detail.short_description ?? '—'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Long description
                </Typography>
                <Typography variant="body1">
                  {detail.long_description ?? '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Tags
                </Typography>
                <Typography variant="body1">{detail.tags ?? '—'}</Typography>
              </Grid>
              {detail.item_type !== 'kit' &&
                ((detail.allows_custom_text ?? 0) !== 0 ||
                  (detail.allows_personalization ?? 0) !== 0) && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Allows custom text
                      </Typography>
                      <Typography variant="body1">
                        {(detail.allows_custom_text ?? 0) !== 0 ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Require Member Selection
                      </Typography>
                      <Typography variant="body1">
                        {(detail.allows_personalization ?? 0) !== 0
                          ? 'Yes'
                          : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Max text length
                      </Typography>
                      <Typography variant="body1">
                        {detail.max_text_length != null
                          ? String(detail.max_text_length)
                          : '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Max text lines
                      </Typography>
                      <Typography variant="body1">
                        {detail.max_text_lines != null
                          ? String(detail.max_text_lines)
                          : '—'}
                      </Typography>
                    </Grid>
                  </>
                )}
              {detail.item_type !== 'kit' && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Has variants (flag)
                  </Typography>
                  <Typography variant="body1">
                    {(detail.has_variants ?? 0) !== 0 ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Images
                </Typography>
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}
                >
                  {(detail.images ?? []).map((img) => (
                    <Box
                      key={img.id}
                      sx={{
                        position: 'relative',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        component="img"
                        src={img.image_url}
                        alt=""
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ px: 0.5, py: 0.25 }}
                      >
                        Order: {img.sort_order}
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutline />}
                        onClick={() => doRemoveImage(img.id)}
                        disabled={isRemovingImage}
                        sx={{ minWidth: 0, py: 0.25 }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      label="Sort order for new image"
                      sx={{ width: 160 }}
                      value={newImageSortOrder}
                      onChange={(e) =>
                        setNewImageSortOrder(
                          e.target.value === '' ? 0 : Number(e.target.value)
                        )
                      }
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddPhotoAlternate />}
                      onClick={handleAddImageClick}
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading…' : 'Add image'}
                    </Button>
                  </Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </Box>
                {uploadError && (
                  <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                    {uploadError}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Sort order
                </Typography>
                <Typography variant="body1">
                  {detail.sort_order ?? '—'}
                </Typography>
              </Grid>
              {detail.item_type !== 'kit' && detail.item_type !== 'variant' && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Library document
                  </Typography>
                  <Typography variant="body1">
                    {detail.library_document_id
                      ? (libraryDocuments.find(
                          (d) => d.id === detail.library_document_id
                        )?.name ?? detail.library_document_id)
                      : '—'}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Max quantity per order
                </Typography>
                <Typography variant="body1">
                  {detail.max_quantity_per_order != null
                    ? String(detail.max_quantity_per_order)
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Categories
                </Typography>
                <Typography variant="body1">
                  {detail.category_ids?.length
                    ? selectedNames(
                        detail.category_ids,
                        categories,
                        Number.POSITIVE_INFINITY
                      )
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Subcategories
                </Typography>
                <Typography variant="body1">
                  {detail.sub_category_ids?.length
                    ? selectedNames(
                        detail.sub_category_ids,
                        allSubcategories,
                        Number.POSITIVE_INFINITY
                      )
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Restrictions
                </Typography>
                <Typography variant="body1">
                  {detail.group_type_ids?.length
                    ? selectedNames(
                        detail.group_type_ids,
                        groupTypes,
                        Number.POSITIVE_INFINITY
                      )
                    : '—'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
